import { useEffect, useCallback, useState, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import './Slider.scss';
import { cn } from '@helpers/styles';
import icons from '@assets/icons/redesign';

const { ArrowLeft, ArrowRight } = icons;

export default function Slider({ slides, visibleSlides = 2.45, loop = false }) {
  // Embla loops on its own. A few copies keep the track full; cloning the
  // slides dozens of times writes every icon into the server-rendered HTML.
  const LOOP_COPIES = 3;
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth !== null && windowWidth <= 768;
  const slideGap = isMobile ? 15 : 30;
  const effectiveVisibleSlides =
    windowWidth !== null && windowWidth <= 1200 ? 1.25 : visibleSlides;

  const baseSlides = useMemo(() => slides, [slides]);
  const emblaSlides = useMemo(
    () => (loop ? Array(LOOP_COPIES).fill(baseSlides).flat() : baseSlides),
    [baseSlides, loop]
  );

  const [emblaRef, embla] = useEmblaCarousel({
    loop,
    align: isMobile ? 'center' : 'start',
    duration: 20
  });

  const initialIndex = loop
    ? baseSlides.length * Math.floor(LOOP_COPIES / 2)
    : 0;

  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  const scrollPrev = useCallback(() => embla?.scrollPrev(), [embla]);
  const scrollNext = useCallback(() => embla?.scrollNext(), [embla]);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelectedIndex(embla.selectedScrollSnap());
  }, [embla]);

  const scrollToIndex = (idx) => {
    if (!embla) return;
    const baseIdx = loop
      ? initialIndex - (initialIndex % baseSlides.length)
      : 0;
    embla.scrollTo(baseIdx + idx);
  };

  useEffect(() => {
    if (!embla) return;
    embla.scrollTo(initialIndex, false);
    embla.on('select', onSelect);
    onSelect();
  }, [embla, onSelect, initialIndex]);

  useEffect(() => {
    if (embla) embla.reInit();
  }, [embla, effectiveVisibleSlides]);

  return (
    <div className="slider">
      <div className="slider-container" ref={emblaRef}>
        <div className="slider-track">
          {emblaSlides.map((slide, idx) => (
            <div
              className="slider-slide"
              key={idx}
              style={{
                flex: `0 0 calc((100% - ${slideGap * (effectiveVisibleSlides - 1)}px) / ${effectiveVisibleSlides})`,
                maxWidth: '440px',
              }}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>

      {!isMobile && (
        <>
          <ArrowLeft
            className={cn('slider-button', 'slider-button-prev', {
              'slider-button-disabled': embla && !embla.canScrollPrev(),
            })}
            onClick={scrollPrev}
          />
          <ArrowRight
            className={cn('slider-button', 'slider-button-next', {
              'slider-button-disabled': embla && !embla.canScrollNext(),
            })}
            onClick={scrollNext}
          />
        </>
      )}

      {isMobile && (
        <div className="slider-dots">
          {baseSlides.map((_, idx) => (
            <span
              key={idx}
              className={cn('slider-dot', {
                'slider-dot-active': idx === selectedIndex % baseSlides.length,
              })}
              onClick={() => scrollToIndex(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function useWindowWidth() {
  const [width, setWidth] = useState(null);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    setWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return width;
}
